import { getCurrent, incrementIndex } from './core.js';

interface State<T> {
  value: T;
  setValue: (val: T | ((prev: T) => T)) => void;
}
type StateAsArray<T> = [T, (val: T | ((prev: T) => T)) => void];

const store: Map<symbol, Map<number, State<any>>> = new Map();

const components: Map<symbol, { requestUpdate: () => void }> = new Map();

export function subscribe(
  componentId: symbol,
  component: { requestUpdate: () => void }
) {
  components.set(componentId, component);
}

export function clearState(componentId: symbol) {
  store.delete(componentId);
  components.delete(componentId);
}

export function useState<T>(
  initialValue: T
): [T, (val: T | ((prev: T) => T)) => void] {
  const [currentComponent, currentIndex] = getCurrent();

  if (!currentComponent) {
    throw new Error('No current component');
  }
  if (!store.has(currentComponent)) {
    store.set(currentComponent, new Map<number, State<T>>());
  }
  const stateMap: Map<number, State<T>> = store.get(currentComponent)!;
  if (!stateMap.has(currentIndex)) {
    stateMap.set(currentIndex, {
      value: initialValue,
      setValue: createSetState(currentComponent, currentIndex),
    });
  }
  const { value, setValue } = stateMap.get(currentIndex)!;

  incrementIndex();

  return [value, setValue];
}

function createSetState<T>(component: symbol, index: number) {
  return function setVal(val: ((prev: T) => T) | T) {
    const state: State<T> | undefined = store.get(component)?.get(index);
    if (!state) {
      return;
    }
    const previousValue = state.value;

    if (typeof val === 'function' && typeof previousValue !== 'function') {
      state.value = (val as (prev: T) => T)(state.value);
    } else {
      state.value = val as T;
    }
    if (previousValue !== state.value) {
      components.get(component)?.requestUpdate();
    }
  };
}

export function useStates<T, U>(
  tValue: T,
  uValue: U
): [StateAsArray<T>, StateAsArray<U>];
export function useStates<T, U, V>(
  tValue: T,
  uValue: U,
  vValue: V
): [StateAsArray<T>, StateAsArray<U>, StateAsArray<V>];
export function useStates<T, U, V, W>(
  tValue: T,
  uValue: U,
  vValue: V,
  wValue: W
): [StateAsArray<T>, StateAsArray<U>, StateAsArray<V>, StateAsArray<W>];
export function useStates<T, U, V, W, Z>(
  tValue: T,
  uValue: U,
  vValue: V,
  wValue: W,
  zValue: Z
): [
  StateAsArray<T>,
  StateAsArray<U>,
  StateAsArray<V>,
  StateAsArray<W>,
  StateAsArray<Z>
];
export function useStates(...initialValues: any[]) {
  return initialValues.map(useState);
}
